import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  AccountBalanceWallet,
  LocalShipping,
  PlaylistAddCheck,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import { formatCurrency } from './KPISummaryCards';

/**
 * EscrowLedgerMonitorPanel
 *
 * Displays:
 *   - Escrow Balance (total across all credit cases)
 *   - Pending Vendor Payments
 *   - Settlement Queue count & value
 */
const EscrowLedgerMonitorPanel = ({ data = {}, loading = false }) => {
  const escrowBalance = data.escrowBalance ?? 0;
  const pendingVendorPayments = data.pendingVendorPayments ?? 0;
  const settlementQueueCount = data.settlementQueueCount ?? 0;
  const settlementQueueValue = data.settlementQueueValue ?? 0;
  const totalDeposits = data.totalDeposits ?? 0;
  const totalWithdrawals = data.totalWithdrawals ?? 0;

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 animate-pulse min-h-[280px]">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-6" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="mb-5">
            <div className="h-4 bg-gray-100 rounded w-full mb-2" />
            <div className="h-6 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  const items = [
    {
      icon: <AccountBalanceWallet sx={{ fontSize: 18 }} />,
      label: 'Escrow Balance',
      value: formatCurrency(escrowBalance),
      chipColor: escrowBalance > 0 ? 'success' : 'warning',
      chipLabel: escrowBalance > 0 ? 'Healthy' : 'Low',
      color: '#10b981',
    },
    {
      icon: <LocalShipping sx={{ fontSize: 18 }} />,
      label: 'Pending Vendor Payments',
      value: formatCurrency(pendingVendorPayments),
      chipColor: pendingVendorPayments > 0 ? 'warning' : 'default',
      chipLabel: pendingVendorPayments > 0 ? 'Pending' : 'Clear',
      color: '#f59e0b',
    },
    {
      icon: <PlaylistAddCheck sx={{ fontSize: 18 }} />,
      label: 'Settlement Queue',
      value: `${settlementQueueCount} pending`,
      chipColor: settlementQueueCount > 5 ? 'error' : 'default',
      chipLabel: formatCurrency(settlementQueueValue),
      color: '#8b5cf6',
    },
  ];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-md transition-shadow duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
            <AccountBalanceWallet sx={{ fontSize: 20, color: '#10b981' }} />
          </div>
          <h3 className="text-sm font-semibold text-gray-700">Escrow Ledger Monitor</h3>
        </div>
      </div>

      {/* Metric cards */}
      <div className="space-y-4">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-xl transition-colors duration-200"
            style={{ backgroundColor: `${item.color}08` }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${item.color}18` }}
              >
                {React.cloneElement(item.icon, { sx: { ...item.icon.props.sx, color: item.color } })}
              </div>
              <div>
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="text-sm font-bold text-gray-900">{item.value}</p>
              </div>
            </div>
            <Chip
              label={item.chipLabel}
              color={item.chipColor}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.6rem', height: 22 }}
            />
          </div>
        ))}
      </div>

      {/* Flow summary */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-3">
          <Tooltip title="Total credit deposited into escrow" arrow>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} />
              <span>Total In: </span>
              <span className="font-semibold text-gray-700">{formatCurrency(totalDeposits)}</span>
            </div>
          </Tooltip>
          <Tooltip title="Total paid out from escrow" arrow>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />
              <span>Total Out: </span>
              <span className="font-semibold text-gray-700">{formatCurrency(totalWithdrawals)}</span>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default EscrowLedgerMonitorPanel;
